// src/pages/admin/Logo.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  Avatar,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../services/api";

/**
 * Resolve maybe-relative url returned by backend to an absolute URL the browser can load.
 * If your backend returns "/uploads/..." we prepend API_BASE.
 * If it's already absolute (http/https) we return it as-is.
 */
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";
function resolveUrl(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    return u.href; // already absolute
  } catch {
    // relative path -> prefix with API_BASE
    return `${API_BASE.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
  }
}

// ==== API helpers ====
async function fetchSettings() {
  const res = await api.get("/rockstar/admin/settings");
  // backend might return object { header_logo: "...", footer_logo: "..." } OR array
  if (Array.isArray(res.data)) {
    const obj = {};
    res.data.forEach((r) => {
      if (r.key) obj[r.key] = r.value;
    });
    return obj;
  }
  return res.data || {};
}

async function uploadFileToServer(key, file, onUploadProgress) {
  const fd = new FormData();
  fd.append("key", key);
  fd.append("file", file);
  const res = await api.post("/rockstar/admin/settings/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  });
  return res.data;
}

export default function LogoSettingsPage() {
  const [headerUrl, setHeaderUrl] = useState("");
  const [footerUrl, setFooterUrl] = useState("");

  const [headerFile, setHeaderFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);

  const [headerPreview, setHeaderPreview] = useState("");
  const [footerPreview, setFooterPreview] = useState("");

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [snack, setSnack] = useState({ open: false, severity: "info", message: "" });

  // load saved settings
  useEffect(() => {
    (async () => {
      try {
        const s = await fetchSettings();
        const h = s.header_logo ?? s.header ?? "";
        const f = s.footer_logo ?? s.footer ?? "";
        setHeaderUrl(h);
        setFooterUrl(f);
        setHeaderPreview(h ? resolveUrl(h) : "");
        setFooterPreview(f ? resolveUrl(f) : "");
      } catch (err) {
        console.error("fetch settings error", err);
        setSnack({ open: true, severity: "error", message: "Failed to load settings" });
      }
    })();
  }, []);

  // create object URL for selected local file previews
  useEffect(() => {
    if (!headerFile) return;
    const url = URL.createObjectURL(headerFile);
    setHeaderPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [headerFile]);

  useEffect(() => {
    if (!footerFile) return;
    const url = URL.createObjectURL(footerFile);
    setFooterPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [footerFile]);

  const handleChooseFile = (which, files) => {
    if (!files?.length) return;
    const f = files[0];
    if (!f.type.startsWith("image/")) {
      setSnack({ open: true, severity: "error", message: "Invalid file type — choose an image" });
      return;
    }
    if (which === "header") {
      setHeaderFile(f);
    } else {
      setFooterFile(f);
    }
  };

  const handleUpload = async (which) => {
    const file = which === "header" ? headerFile : footerFile;
    if (!file) {
      setSnack({ open: true, severity: "warning", message: "Choose a file first" });
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const data = await uploadFileToServer(
        which + "_logo",
        file,
        (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        }
      );

      // backend might return { url: "/uploads/..." } or { path: "..."} or { header_logo: "..." }
      let url = data?.url || data?.path || data?.header_logo || data?.footer_logo || null;
      if (!url && typeof data === "string") {
        // some endpoints might return a plain path string
        url = data;
      }

      if (!url) {
        throw new Error("Upload succeeded but no URL returned by server");
      }

      // update saved url and preview (resolve to absolute for preview)
      if (which === "header") {
        setHeaderUrl(url);
        setHeaderPreview(resolveUrl(url));
        setHeaderFile(null);
      } else {
        setFooterUrl(url);
        setFooterPreview(resolveUrl(url));
        setFooterFile(null);
      }

      setSnack({ open: true, severity: "success", message: "Logo uploaded successfully" });
    } catch (err) {
      console.error("upload error", err);
      setSnack({ open: true, severity: "error", message: "Upload failed" });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // remove the locally selected file (not saved)
  const handleRemoveSelected = (which) => {
    if (which === "header") setHeaderFile(null);
    else setFooterFile(null);
  };

  // optional: clear saved logo on server (if you implement an endpoint)
  async function handleClearSaved(which) {
    try {
      const key = which === "header" ? "header_logo" : "footer_logo";
      // expects backend to accept { key, value: null } to clear
      await api.post("/rockstar/admin/settings", { key, value: null });
      if (which === "header") {
        setHeaderUrl("");
        setHeaderPreview("");
      } else {
        setFooterUrl("");
        setFooterPreview("");
      }
      setSnack({ open: true, severity: "success", message: "Saved logo cleared" });
    } catch (err) {
      console.error("clear saved logo error", err);
      setSnack({ open: true, severity: "error", message: "Failed to clear logo" });
    }
  }

  const LogoCard = ({ label, preview, file, onChoose, onUpload, onRemoveSelected, onClearSaved, busy }) => (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        textAlign: "center",
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box>
        <Avatar
          variant="rounded"
          src={preview || "/images/default.png"}
          sx={{
            width: "100%",
            height: 96,
            borderRadius: 1.5,
            mb: 1,
            bgcolor: "grey.900",
            border: "1px solid rgba(255,255,255,0.08)",
            objectFit: "contain",
          }}
        />
        <Typography variant="subtitle1" gutterBottom>
          {label}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} size="small">
          Choose
          <input hidden type="file" accept="image/*" onChange={(e) => onChoose(e.target.files)} />
        </Button>

        <Button variant="contained" color="primary" startIcon={<SaveIcon />} size="small" onClick={onUpload} disabled={!file || busy}>
          {busy ? "Uploading..." : "Save"}
        </Button>

        <Tooltip title="Remove selected file (not saved)">
          <IconButton color="error" size="small" onClick={onRemoveSelected}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>

        <Button variant="text" size="small" onClick={onClearSaved}>
          Clear saved
        </Button>
      </Box>

      {busy && file && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption">{progress}%</Typography>
        </Box>
      )}
    </Paper>
  );

  return (
    <Box sx={{ pt: { xs: 10, sm: 12 }, pb: 6, display: "flex", justifyContent: "center" }}>
      <Container maxWidth="md">
        <Typography variant="h5" fontWeight={600} align="center" gutterBottom>
          Manage Site Logos
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Upload header and footer logos with live preview. Click "Clear saved" to remove the saved logo.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <LogoCard
              label="Header Logo"
              preview={headerPreview}
              file={headerFile}
              onChoose={(files) => handleChooseFile("header", files)}
              onUpload={() => handleUpload("header")}
              onRemoveSelected={() => handleRemoveSelected("header")}
              onClearSaved={() => handleClearSaved("header")}
              busy={uploading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LogoCard
              label="Footer Logo"
              preview={footerPreview}
              file={footerFile}
              onChoose={(files) => handleChooseFile("footer", files)}
              onUpload={() => handleUpload("footer")}
              onRemoveSelected={() => handleRemoveSelected("footer")}
              onClearSaved={() => handleClearSaved("footer")}
              busy={uploading}
            />
          </Grid>
        </Grid>
      </Container>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
